/**
 * VehicleServiceHistoryCard Component
 *
 * Shows previous work orders for the same vehicle.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import HistoryIcon from '@mui/icons-material/History';
import { format } from 'date-fns';
import { useWorkOrders, type WorkOrderListItem } from '@/hooks/useWorkOrders';
import { navigateAwayFromWorkOrderDetail } from '@/lib/workOrderNavigation';
import { statusColors, neutralGrey } from '@/styles/theme';
import { dateFnsLocale } from '@/utils/locale';

interface VehicleServiceHistoryCardProps {
  vehicleId: string;
  currentWorkOrderId: string;
  cardVariant?: 'elevation' | 'outlined';
}

const statusIconColor = (status: string): string =>
  (statusColors as Record<string, string>)[status] ?? neutralGrey;

const formatPrice = (cents: number) => `${(cents / 100).toFixed(2)} €`;

export function VehicleServiceHistoryCard({
  vehicleId,
  currentWorkOrderId,
  cardVariant = 'elevation',
}: VehicleServiceHistoryCardProps) {
  const { t } = useTranslation('workorders');
  const navigate = useNavigate();

  const { data, isLoading } = useWorkOrders(
    { vehicleId, sortBy: 'scheduledAt', sortOrder: 'desc', limit: 10 },
    { enabled: !!vehicleId },
  );

  const workOrders = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages
      .flatMap((p) => p.data)
      .filter((wo: WorkOrderListItem) => wo.id !== currentWorkOrderId);
  }, [data, currentWorkOrderId]);

  return (
    <Card variant={cardVariant} sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('vehicleServiceHistory')}
        </Typography>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : workOrders.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">{t('noServiceHistory')}</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {workOrders.map((wo) => {
              const color = statusIconColor(wo.status);
              const serviceNames = wo.services?.map((s) => s.name).join(', ') ?? wo.serviceType;
              const totalCents = wo.totalPartsCents + wo.totalLaborCents;
              return (
                <ListItemButton
                  key={wo.id}
                  onClick={() =>
                    navigateAwayFromWorkOrderDetail(
                      window.location.pathname,
                      `/workorders/${wo.id}`,
                      navigate,
                    )
                  }
                  sx={{ px: 0, py: 1, borderRadius: 1 }}
                >
                  <ListItemAvatar sx={{ minWidth: 44 }}>
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: alpha(color, 0.12),
                        color,
                      }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: color,
                        }}
                      />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="body2" noWrap>
                        {serviceNames}
                      </Typography>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary" component="span">
                          {format(new Date(wo.scheduledAt), 'd.M.yyyy', { locale: dateFnsLocale() })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="span">
                          {t(`status.${wo.status}`, wo.status)}
                        </Typography>
                        {totalCents > 0 && (
                          <Typography variant="caption" color="text.secondary" component="span">
                            {formatPrice(totalCents)}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </CardContent>
    </Card>
  );
}
